/**
 * ADMIN TASK SERVICE
 * 
 * Handles all admin task management operations including:
 * - Creating system-generated and manual tasks
 * - Assigning tasks to users/roles
 * - Updating task status
 * - Auto-completion logic
 * - Task notifications
 */

import { getSessionHeaders } from '../serverSession';

// API helper for admin task operations - NO getServiceRoleClient() in browser
async function callTasksAPI(operation: string, data: Record<string, any> = {}): Promise<any> {
 const response = await fetch('/api/admin/tasks', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, data })
 })
 const result = await response.json()
 if (!response.ok) {
 throw new Error(result.error || 'API call failed')
 }
 return result
}

export interface AdminTask {
 id: string
 title: string
 description?: string
 task_type: 'system_generated' | 'manual'
 source_type?: string
 source_id?: string
 source_table?: string
 priority: 'low' | 'medium' | 'high' | 'urgent'
 status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
 assigned_to_role?: string
 assigned_to_user_id?: string
 due_date?: string
 created_at: string
 updated_at: string
 completed_at?: string
 created_by_user_id?: string
 completed_by_user_id?: string
 metadata?: any
 auto_complete_enabled: boolean
}

export interface CreateTaskInput {
 title: string
 description?: string
 task_type: 'system_generated' | 'manual'
 source_type?: string
 source_id?: string
 source_table?: string
 priority?: 'low' | 'medium' | 'high' | 'urgent'
 assigned_to_role?: string
 assigned_to_user_id?: string
 due_date?: string
 created_by_user_id?: string
 metadata?: any
}

export interface TaskFilters {
 status?: string[]
 priority?: string[]
 assigned_to_role?: string
 assigned_to_user_id?: string
 source_type?: string[]
 search?: string
}

export interface TaskStatistics {
 total: number
 pending: number
 in_progress: number
 completed: number
 cancelled: number
 by_priority: {
 urgent: number
 high: number
 medium: number
 low: number
 }
 by_source_type: Record<string, number>
 overdue: number
}

class AdminTaskService {
 /**
 * Get all tasks with optional filters
 */
 async getTasks(filters?: TaskFilters, limit = 100, offset = 0): Promise<{ tasks: AdminTask[], total: number }> {
 try {
 const result = await callTasksAPI('getTasks', { filters, limit, offset })
 return { tasks: result.tasks || [], total: result.total || 0 }
 } catch (error) {
 console.error('Error fetching tasks:', error)
 throw error
 }
 }

 /**
 * Get task statistics
 */
 async getTaskStatistics(): Promise<TaskStatistics> {
 try {
 return await callTasksAPI('getTaskStatistics')
 } catch (error) {
 console.error('Error fetching task statistics:', error)
 throw error
 }
 }

 /**
 * Create a new task
 */
 async createTask(input: CreateTaskInput): Promise<AdminTask> {
 try {
 const result = await callTasksAPI('createTask', { input })
 return result.task
 } catch (error) {
 console.error('Error creating task:', error)
 throw error
 }
 }

 /**
 * Update task status
 */
 async updateTaskStatus(taskId: string, status: string, userId?: string): Promise<void> {
 try {
 await callTasksAPI('updateTaskStatus', { taskId, status, userId })
 } catch (error) {
 console.error('Error updating task status:', error)
 throw error
 }
 }

 /**
 * Update task assignment
 */
 async updateTaskAssignment(taskId: string, assignedToUserId?: string, assignedToRole?: string): Promise<void> {
 try {
 await callTasksAPI('updateTaskAssignment', { taskId, assignedToUserId, assignedToRole })
 } catch (error) {
 console.error('Error updating task assignment:', error)
 throw error
 }
 }

 /**
 * Delete a task
 */
 async deleteTask(taskId: string): Promise<void> {
 try {
 await callTasksAPI('deleteTask', { taskId })
 } catch (error) {
 console.error('Error deleting task:', error)
 throw error
 }
 }

 /**
 * Sync system-generated tasks (create tasks for pending items)
 */
 async syncSystemTasks(): Promise<void> {
 try {
 await callTasksAPI('syncSystemTasks')
 } catch (error) {
 console.error('Error syncing system tasks:', error)
 throw error
 }
 }

 /**
 * Get unread task notifications for a user
 */
 async getUnreadNotifications(userId: string): Promise<number> {
 try {
 const result = await callTasksAPI('getUnreadNotifications', { userId })
 return result.count || 0
 } catch (error) {
 console.error('Error fetching unread notifications:', error)
 return 0
 }
 }
}

export const adminTaskService = new AdminTaskService()

